import React from "react";
import { FileText } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { MARKING_SECTIONS, toMr, sectionTotal } from "@/lib/markingPattern";

export const MARKING_PDF = "/marking-pattern.pdf";

const GRAND_TOTAL = MARKING_SECTIONS.reduce((a, s) => a + sectionTotal(s), 0);

function SectionCard({ sec }) {
  const total = sectionTotal(sec);
  const isExp = !!sec.exp;
  return (
    <div className="overflow-hidden rounded-xl border border-[#e6dcc4] bg-white" data-testid={`marking-section-${sec.title[0]}`}>
      <div className="flex items-center justify-between gap-3 bg-[#1b5e3f] px-4 py-2.5">
        <p className="text-[13.5px] font-bold text-white sm:text-[14.5px]">{sec.title}</p>
        <span className="shrink-0 rounded-full bg-white/15 px-3 py-0.5 text-[12.5px] font-bold text-white">{toMr(total)} गुण</span>
      </div>
      <table className="w-full border-collapse text-[13px]">
        <thead>
          <tr className="bg-[#f3efe2] text-left text-[#1b3d2a]">
            <th className="w-12 border-b border-[#e6dcc4] px-3 py-2 text-center font-semibold">अ.क्र.</th>
            <th className="border-b border-[#e6dcc4] px-3 py-2 font-semibold">{isExp ? "अनुभव" : "पुरावा / निकष"}</th>
            {isExp ? (
              <>
                <th className="w-28 border-b border-l border-[#e6dcc4] px-2 py-2 text-center font-semibold">एक वर्षाच्या आतील</th>
                <th className="w-28 border-b border-l border-[#e6dcc4] px-2 py-2 text-center font-semibold">एक वर्षाच्या वरील</th>
              </>
            ) : (
              <th className="w-20 border-b border-l border-[#e6dcc4] px-3 py-2 text-center font-semibold">गुण</th>
            )}
          </tr>
        </thead>
        <tbody>
          {sec.items.map((it, i) => (
            <tr key={i} className="align-top odd:bg-white even:bg-[#faf8f0]">
              <td className="border-b border-[#eee3c9] px-3 py-2 text-center text-slate-500">{toMr(i + 1)}</td>
              <td className="border-b border-[#eee3c9] px-3 py-2 text-slate-700">{it[0]}</td>
              {isExp ? (
                <>
                  <td className="border-b border-l border-[#eee3c9] px-2 py-2 text-center font-semibold text-[#1b5e3f]">{toMr(it[2])}</td>
                  <td className="border-b border-l border-[#eee3c9] px-2 py-2 text-center font-semibold text-[#1b5e3f]">{toMr(it[1])}</td>
                </>
              ) : (
                <td className="border-b border-l border-[#eee3c9] px-3 py-2 text-center font-semibold text-[#1b5e3f]">{toMr(it[1])}</td>
              )}
            </tr>
          ))}
          <tr className="bg-[#eef5ea] font-bold text-[#1b3d2a]">
            <td className="px-3 py-2" />
            <td className="px-3 py-2 text-right">एकूण</td>
            {isExp ? (
              <>
                <td className="border-l border-[#e6dcc4] px-2 py-2 text-center">{toMr(total)}</td>
                <td className="border-l border-[#e6dcc4] px-2 py-2 text-center">{toMr(total)}</td>
              </>
            ) : (
              <td className="border-l border-[#e6dcc4] px-3 py-2 text-center">{toMr(total)}</td>
            )}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export default function MarkingPatternModal({ open, onOpenChange }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[95vh] w-[96vw] max-w-4xl flex-col gap-0 overflow-hidden p-0" data-testid="marking-pattern-modal">
        <div className="flex items-center gap-2.5 border-b-2 border-[#f1ead7] bg-[#fbf9f2] px-5 py-4">
          <FileText className="h-5 w-5 shrink-0 text-[#1b5e3f]" />
          <DialogTitle className="font-head flex-1 text-[16px] font-semibold text-[#1b5e3f] sm:text-[18px]">
            निवड प्रक्रिया — गुण विभागणी (एकूण {toMr(GRAND_TOTAL)} गुण)
          </DialogTitle>
          <DialogDescription className="sr-only">अर्ज भरण्यापूर्वी गुण विभागणी तक्ता वाचा.</DialogDescription>
        </div>
        <div className="bg-[#fdf7e5] px-5 py-2.5 text-[13px] leading-relaxed text-[#8a5a06]">
          कृपया अर्ज भरण्यापूर्वी खालील <strong>गुण विभागणी तक्ता</strong> काळजीपूर्वक वाचा. प्रत्येक टप्प्यातील माहिती व कागदपत्रे यानुसार गुण दिले जातात.
        </div>
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto bg-slate-50 px-4 py-4 sm:px-5">
          {MARKING_SECTIONS.map((sec) => <SectionCard key={sec.title} sec={sec} />)}
        </div>
        <div className="flex justify-end border-t-2 border-[#f1ead7] bg-[#fbf9f2] px-5 py-3">
          <button
            onClick={() => onOpenChange(false)}
            data-testid="marking-close-btn"
            className="rounded-lg bg-[#5a9e3a] px-6 py-2 text-[14px] font-semibold text-white hover:opacity-90"
          >
            वाचले, अर्ज भरा
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
